import { Link } from "react-router-dom";
import { useCallback, useContext, useState, useEffect, useMemo } from "react";
import { useMediaQuery } from "react-responsive";
import Swal from "sweetalert2";

// Icons
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, NoSymbolIcon } from "@heroicons/react/24/solid";

// Context
import LoaderContext from "../../../context/psikotest/LoaderContext";
import SnackbarContext from "../../../context/psikotest/SnackbarContext";

// Helper
import errorHandler from "../../../helper/psikotest/errorHandler";
import logRender from "../../../helper/psikotest/logRender";

// Controller
import Psikolog from "../../../controller/psikotest/psikotest_system/psikolog";

// Component
import TableFilter from "../../../components/psikotest/psikotest_system/TableFilter";
import Table from "../../../components/psikotest/psikotest_system/Table";
import { SnackbarObject } from "../../../context/psikotest/Type";

function List() {
  const { setMessage } = useContext(LoaderContext);
  const setNotif = useContext(SnackbarContext).setNotif;
  const [list, setList] = useState<Array<TPSPsikologDetail>>([]);
  const [search, setSearch] = useState<string>("");

  useEffect(() => {
    logRender({ type: "page", name: "psikolog/List" });
  }, []);

  const getList = useCallback(
    async (setNotif: React.Dispatch<React.SetStateAction<SnackbarObject>>) => {
      try {
        setMessage("Fetch Psikolog");
        const ac = new Psikolog();
        const res = await ac.list();
        setList(res.data.list);
        setMessage("");
      } catch (error) {
        setMessage("");
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [setMessage]
  );

  useEffect(() => {
    getList(setNotif);
  }, [getList, setNotif]);

  const handleDeleteList = (id: number) => {
    const arrFilter = [...list].filter((v) => v.id !== id);
    setList(arrFilter);
  };

  return (
    <div className="w-full flex flex-col space-y-0.5">
      <div className="flex flex-col md:flex-row items-start md:items-center">
        <Link
          to={`create`}
          className="block w-auto mb-2 md:m-0 px-4 py-2 bg-green-500 hover:bg-green-700 text-white rounded font-semibold text-sm"
        >
          <div className="flex">
            <PencilIcon className="w-5 h-5 mr-2 stroke-white fill-tranparent" aria-hidden="true" />
            Create
          </div>
        </Link>
        <div className="w-full md:w-auto ml-auto flex">
          <TableFilter search={setSearch} />
        </div>
      </div>
      <div>
        <CategoryList list={list} handleDeleteList={handleDeleteList} search={search} />
      </div>
    </div>
  );
}

function CategoryList(props: { list: Array<TPSPsikologDetail>; handleDeleteList: (id: number) => void; search: string }) {
  const setNotif = useContext(SnackbarContext).setNotif;
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const confirmDelete = useCallback(
    async (psikolog: TPSPsikologDetail) => {
      try {
        const confirm = await Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes, delete it!",
        });
        if (confirm.isConfirmed) {
          const ac = new Psikolog();
          await ac.delete(psikolog.user_id.toString());
          props.handleDeleteList(psikolog.id);
          setNotif({ type: "success", message: "psikolog deleted" });
        }
      } catch (error) {
        const errorMessage = errorHandler(error);
        setNotif({ type: "error", message: errorMessage });
      }
    },
    [props, setNotif]
  );

  const columns = useMemo(
    () => [
      {
        id: "mobile",
        Header: "Psikolog",
        accessor: "id",
        show: isMobile,
        Cell: (props: { row: { original: TPSPsikologDetail } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.full_name || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "full_name",
        classHeader: "text-left px-2",
        Header: "Full Name",
        classDivHeader: "flex items-center",
        accessor: "full_name",
        show: !isMobile,
        Cell: (props: { row: { original: TPSPsikologDetail } }) => {
          return (
            <Link to={`${props.row.original.id}`} className="text-blue-500 hover:text-blue-800">
              {props.row.original.full_name || "NULL"}
            </Link>
          );
        },
      },
      {
        id: "username",
        classHeader: "text-left px-2",
        Header: "Username",
        classDivHeader: "flex items-center",
        accessor: "username",
        show: !isMobile,
      },
      {
        id: "locked",
        classHeader: "w-40 text-left px-2",
        Header: "Status Account",
        classDivHeader: "flex items-center",
        accessor: "locked",
        show: !isMobile,
        Cell: (props: { row: { original: TPSPsikologDetail } }) => {
          return props.row.original.locked === "1" ? (
            <div className="flex m-auto text-red-500 w-5" title="User Locked (can't login)">
              <NoSymbolIcon />
            </div>
          ) : (
            <div className="flex m-auto text-green-500 w-5" title="User Active">
              <CheckCircleIcon />
            </div>
          );
        },
      },
      {
        id: "action_delete",
        classHeader: "w-[30px] text-white",
        Header: () => {
          return <TrashIcon className="h-5" />;
        },
        classDivHeader: "flex justify-center",
        accessor: "id",
        show: !isMobile,
        disableSortBy: true,
        className: "text-white bg-red-500 hover:bg-red-800",
        Cell: (props: { row: { original: TPSPsikologDetail } }) => {
          return (
            <button className="flex justify-center" onClick={() => confirmDelete(props.row.original)}>
              <TrashIcon className="h-5" />
            </button>
          );
        },
      },
    ],
    [isMobile, confirmDelete]
  );

  return <Table columns={columns} list={props.list} search={props.search} />;
}

export default List;
